/**
 * Which flights the user has unchecked in the toolbar.
 *
 * ## Why this can't live on the DataSource
 *
 * A flight has TWO possible Cesium data sources — `<id>` and `<id>_colored` —
 * and only one of them exists at a time. Switching the Vario Track destroys one
 * and builds the other (`CreateDataSource.switchColorDataSource`), so a
 * `show = false` written to the one that gets destroyed is simply gone, and the
 * replacement is created visible. Measured on a 10-pilot flight: hide pilot A
 * while A is the Vario Track, then colour pilot B, and A's plain track pops
 * back on screen while its checkbox still reads unchecked.
 *
 * So the hidden set is keyed by flight id and kept here; every place that
 * creates or swaps a data source asks it what the visibility should be. It is
 * module-level rather than React state because data sources are created in a
 * promise callback outside the render flow and need a synchronous read — the
 * toolbar just subscribes to re-render.
 *
 * The checkbox is the master switch: a hidden flight stays hidden even when it
 * is selected as the Vario Track.
 */

const hidden = new Set();
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

/** True when the user has unchecked this flight. */
export const isFlightHidden = (id) => hidden.has(id);

export const setFlightHidden = (id, value) => {
  if (value === hidden.has(id)) return;
  if (value) hidden.add(id);
  else hidden.delete(id);
  notify();
};

/** Forget flights that are no longer loaded, so the set can't grow forever. */
export const pruneFlightVisibility = (ids) => {
  const keep = new Set(ids);
  let changed = false;
  hidden.forEach((id) => {
    if (!keep.has(id)) {
      hidden.delete(id);
      changed = true;
    }
  });
  if (changed) notify();
};

/** Subscribe to changes (returns an unsubscribe). */
export const onFlightVisibilityChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
